import { SSOAPIContext } from 'helpers/context/ssoAPIContext';
import { IntegrationType } from 'helpers/integration/IntegrationType';
import { ClientResponse } from './client-response';
import { SSOAPINetworkError } from 'services/errors';
export class Client {
    private baseSSOURL: URL;
    private uiVersion: string;
    private tenantId: string;
    private integrationType: IntegrationType;
    private sessionHeaders: { [key: string]: unknown };
    constructor(ssoURL: string, uiVersion: string, tenantId: string, integrationType: IntegrationType, sessionHeaders: { [key: string]: unknown }) {
        this.baseSSOURL = new URL(ssoURL);
        this.uiVersion = uiVersion;
        this.tenantId = tenantId;
        this.integrationType = integrationType;
        this.sessionHeaders = sessionHeaders;
    }

    /**
     * Makes a POST request to the specified URL path.
     * @param {string} urlPath - The URL path for the request. This path will be combined with a base URL defined elsewhere to form the complete URL.
     * @param {RequestInit} options - An optional RequestInit object to customize the fetch request. This can include custom headers, method, mode, credentials, etc.
     * @param {SSOAPIContext} [ssoAPIContext] - Optional context specific to the SSO API.
     * @returns {Promise<Response>} The response from the request.
     */
    public post(urlPath: string, options: RequestInit, ssoAPIContext?: SSOAPIContext): Promise<ClientResponse> {
        return this.sendRequest('POST', urlPath, options, ssoAPIContext);
    }


    /**
     * Makes a GET request to the specified URL path, optionally including query parameters and custom request options.
     * This method constructs the full URL with query parameters, sets default headers, and merges any additional request options provided.
     *
     * @param urlPath The URL path for the request. This path will be combined with a base URL defined elsewhere to form the complete URL.
     * @param requestOptions An optional object that may include:
     *   - queryParams: An object containing query parameters as key-value pairs, where the key is the parameter name and the value is its corresponding value. The value can be of type string, number, or boolean.
     *   - options: An optional RequestInit object to customize the fetch request. This can include custom headers, method, mode, credentials, etc.
     * @returns A Promise that resolves to the Response object from the fetch request.
     */
    public async get(urlPath: string, requestOptions?: { queryParams?: { [key: string]: string | number | boolean }, options?: RequestInit }): Promise<ClientResponse> {
        const url = this.createURL(urlPath);
        if (requestOptions?.queryParams) {
            for (const [key, value] of Object.entries(requestOptions.queryParams)) {
                url.searchParams.append(key, value.toString());
            }
        }

        const baseHeaders = {
            ...this.sessionHeaders,
            tenantid: this.tenantId,
            integrationtype: this.integrationType,
            uiversion: this.uiVersion,
        };
        const finalOptions = { ...requestOptions?.options, method: 'GET', headers: { ...requestOptions?.options?.headers, ...baseHeaders } };
        const requestStartTimeMS = Date.now();
        try {
            const response = await fetch(url.href, finalOptions);
            const requestEndTimeMS = Date.now();
            return ClientResponse.fromResponse(response, requestStartTimeMS, requestEndTimeMS);
        } catch (err) {
            throw new SSOAPINetworkError(`An error occurred while processing the request: ${err instanceof Error ? err.message : JSON.stringify(err)}.`, { cause: err as Error, requestStartTimeMS: requestStartTimeMS, requestEndTimeMS: Date.now() });
        }

    }

    /**
     * Makes a PUT request to the specified URL path.
     * @param {string} urlPath - The URL path for the request. This path will be combined with a base URL defined elsewhere to form the complete URL.
     * @param {RequestInit} options - An optional RequestInit object to customize the fetch request. This can include custom headers, method, mode, credentials, etc.
     * @param {SSOAPIContext} [ssoAPIContext] - Optional context specific to the SSO API.
     * @returns {Promise<Response>} The response from the request.
     */
    public put(urlPath: string, options: RequestInit, ssoAPIContext?: SSOAPIContext): Promise<ClientResponse> {
        return this.sendRequest('PUT', urlPath, options, ssoAPIContext);
    }

    /**
     * Makes a DELETE request to the specified URL path.
     * @param {string} urlPath - The URL path for the request. This path will be combined with a base URL defined elsewhere to form the complete URL.
     * @param {RequestInit} options - An optional RequestInit object to customize the fetch request. This can include custom headers, method, mode, credentials, etc.
     * @param {SSOAPIContext} [ssoAPIContext] - Optional context specific to the SSO API.
     * @returns {Promise<Response>} The response from the request.
     */
    public delete(urlPath: string, options: RequestInit, ssoAPIContext?: SSOAPIContext): Promise<ClientResponse> {
        return this.sendRequest('DELETE', urlPath, options, ssoAPIContext);
    }

    /**
     * General method for sending HTTP requests using the Fetch API.
     * @param {string} method - The HTTP method to be used (e.g., 'POST', 'PUT', 'DELETE').
     * @param {string} urlPath - The URL path for the request. This path will be combined with a base URL defined elsewhere to form the complete URL.
     * @param {RequestInit} options - An optional RequestInit object to customize the fetch request. This can include custom headers, method, mode, credentials, etc.
     * @param {SSOAPIContext} [ssoAPIContext] - Optional context specific to the SSO API.
     * @returns {Promise<Response>} The response from the server.
     * @private
     */
    private async sendRequest(method: string, urlPath: string, options: RequestInit, ssoAPIContext?: SSOAPIContext): Promise<ClientResponse> {
        const url = this.createURL(urlPath);
        const baseHeaders = {
            ...this.sessionHeaders,
            'Content-Type': 'application/json',
            tenantid: this.tenantId,
            integrationtype: this.integrationType,
            uiversion: this.uiVersion,
            ...(ssoAPIContext?.eventId ? { eventid: ssoAPIContext?.eventId } : {}),
        };
        const finalOptions = { ...options, method: method, headers: { ...options.headers, ...baseHeaders } };

        const requestStartTimeMS = Date.now();

        try {
            const response = await fetch(url.href, finalOptions);
            const requestEndTimeMS = Date.now();
            return ClientResponse.fromResponse(response, requestStartTimeMS, requestEndTimeMS);
        } catch (err) {
            throw new SSOAPINetworkError(`An error occurred submitting your request: ${err instanceof Error ? err.message : JSON.stringify(err)}` , { cause: err as Error, requestStartTimeMS: requestStartTimeMS, requestEndTimeMS: Date.now() });
        }
    }

    /**
     * Creates a full URL based on the base SSO URL and a specific path.
     * @param {string} urlPath - The specific path to append to the base URL.
     * @returns {URL} The complete URL.
     * @private
     */
    private createURL(urlPath: string): URL {
        const url = new URL(this.baseSSOURL.href);
        if (url.pathname.endsWith('/') && urlPath.startsWith('/')) {
            if (url.pathname.length > 1) {
                url.pathname = url.pathname.substr(0, url.pathname.length - 1);
            } else if (urlPath.length > 1) {
                urlPath = urlPath.substr(1, urlPath.length);
            } else {
                urlPath = '';
            }
        } else if (!url.pathname.endsWith('/') && !urlPath.startsWith('/')) {
            urlPath = '/' + urlPath;
        }
        url.pathname = url.pathname + urlPath;
        return url;
    }
}
